'use client';

import { HttpError } from 'http-errors';
import isEqual from 'lodash.isequal';
import merge from 'lodash.merge';
import { usePathname } from 'next/navigation';
import pLimit from 'p-limit';
import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLogger, useTracking } from '@/analytics';
import { stripNulls } from '@/helpers/formatters';
import { getLoadTime } from '@/helpers/meta';
import { useConciergePhone } from '@/hooks';
import { useVisitorCookie } from '@/hooks/useVisitorCookie';
import { Geolocation, MetaValues, SavedVisitor, Visitor, VisitorCookie } from '@/models';
import { persistFirstLoad, persistVisitor } from '@/server/loadVisitorSsr';

import { getVid } from './apiHeaders';
import { initBotCheck, isBotIdPath, sendBotCheck } from './botId';

// A save never refreshes the current page; call refreshVisitorSsr() when the next render must read it
type UpdateOptions = { persist?: boolean };

export type PersistStrategy = {
  persistFirstLoad: (meta: MetaValues) => Promise<{ geolocation?: Geolocation }>;
  persistVisitor: (updates: Partial<Visitor>) => Promise<SavedVisitor | null>;
};

export type VisitorContextType = {
  conciergePhone?: string;
  savingCounter: number;
  updateVisitor: (updates: Partial<Visitor>, opts?: UpdateOptions) => Promise<SavedVisitor | null | undefined>;
  visitor?: Visitor;
};

export const VisitorContext = createContext<VisitorContextType>({
  savingCounter: 0,
  updateVisitor: async () => undefined,
});

if (typeof window !== 'undefined' && isBotIdPath(window.location.pathname)) {
  initBotCheck();
}

const marketplacePersist: PersistStrategy = {
  persistFirstLoad,
  persistVisitor,
};

type Props = {
  children: ReactNode;
  persist?: PersistStrategy;
  reserveConciergePhone?: boolean;
  visitorCookie?: VisitorCookie;
  writeCookies?: boolean;
};

export function VisitorProvider({
  children,
  persist = marketplacePersist,
  reserveConciergePhone = true,
  visitorCookie,
  writeCookies = true,
}: Props) {
  const { setVisitorCookie } = useVisitorCookie();
  // TODO: Replace this with useOptimistic when client cookie writes are removed.
  const [visitor, setVisitor] = useState<Visitor | undefined>(visitorCookie);

  const visitorRef = useRef<Visitor | undefined>(visitor);

  const conciergePhone = useConciergePhone({ enabled: reserveConciergePhone });
  const { compileMetaValues, initTracking, trackEvent } = useTracking();
  const { addError } = useLogger();
  const pathname = usePathname();

  // TODO: Remove savingCounter once call sites switch to page-level pending state.
  const [savingCounter, setSavingCounter] = useState(0);
  const rateLimit = useMemo(() => pLimit(1), []);
  const errorHandler = useErrorHandler(writeCookies);

  // The route already saved the verdict, so only the local visitor and cookie need it
  const saveBotCheck = useCallback(async () => {
    const is_likely_bot = await sendBotCheck();
    if (is_likely_bot === undefined) {
      return;
    }

    visitorRef.current = merge({}, visitorRef.current, { meta_values: { is_likely_bot } });
    if (writeCookies) {
      setVisitorCookie({ meta_values: { is_likely_bot } });
    }
    setVisitor(visitorRef.current);
  }, [setVisitorCookie, writeCookies]);

  const saveFirstLoadMeta = useCallback(async () => {
    try {
      setSavingCounter((counter) => counter + 1);

      const landing_load_time = getLoadTime();
      const meta_values = await compileMetaValues({ landing_load_time });

      const { geolocation } = await persist.persistFirstLoad(meta_values);

      // use merge over spread to avoid squashing nested fields
      visitorRef.current = merge({}, visitorRef.current, { geolocation, meta_values });
      if (writeCookies) {
        setVisitorCookie({ geolocation, meta_values });
      }
      setVisitor(visitorRef.current);

      // After the save, so the two writes to the visitor don't race
      if (visitorRef.current.meta_values?.is_likely_bot == null && isBotIdPath(pathname)) {
        saveBotCheck();
      }
    } catch (err) {
      errorHandler({ action: 'saveFirstLoadMeta', err });
    } finally {
      setSavingCounter((counter) => counter - 1);
    }
  }, [compileMetaValues, errorHandler, pathname, persist, saveBotCheck, setVisitorCookie, writeCookies]);

  // reload the visitor to save client side meta value
  const isInitialized = useRef(false);
  useEffect(() => {
    if (isInitialized.current || !visitor?.vid) {
      return;
    }

    isInitialized.current = true;

    initTracking(visitor).catch((err) => addError(err, { action: 'initTracking', visitor }));

    rateLimit(saveFirstLoadMeta);
  }, [addError, initTracking, rateLimit, saveFirstLoadMeta, trackEvent, visitor]);

  const updateVisitor = useCallback(
    async (updates: Partial<Visitor>, opts?: UpdateOptions) => {
      // use merge over spread to avoid squashing nested fields
      const newValue = merge({}, visitorRef.current, updates);

      if (isEqual(newValue, visitorRef.current)) {
        return;
      }

      // Optimistically update React state & cookie
      visitorRef.current = newValue;
      setVisitor(newValue);
      if (writeCookies) {
        setVisitorCookie(newValue);
      }

      if (opts?.persist === false) {
        return;
      }

      try {
        setSavingCounter((counter) => counter + 1);

        const meta_values = await compileMetaValues();

        const data = await rateLimit(async () => persist.persistVisitor({ ...updates, meta_values }));

        const updatedValue = merge({}, visitorRef.current, data);

        if (!isEqual(visitorRef.current, updatedValue)) {
          visitorRef.current = updatedValue;
          if (writeCookies) {
            setVisitorCookie(updatedValue);
          }
          setVisitor(updatedValue);
        }

        return data;
      } catch (err) {
        errorHandler({ action: 'updateVisitor', err, updates });
      } finally {
        setSavingCounter((counter) => counter - 1);
      }
    },
    [compileMetaValues, errorHandler, persist, rateLimit, setVisitorCookie, writeCookies]
  );

  return (
    <VisitorContext.Provider value={{ conciergePhone, savingCounter, updateVisitor, visitor }}>
      {children}
    </VisitorContext.Provider>
  );
}

const useErrorHandler = (writeCookies: boolean) => {
  const { resetCookies } = useVisitorCookie();
  const { addError, logger } = useLogger();

  return useCallback(
    ({ action, err, updates }: { action: string; err: unknown; updates?: Partial<Visitor> }) => {
      const error = err as HttpError;
      const { message = '', name, status } = error ?? {};
      const context = stripNulls({ action, component: 'VisitorProvider', updates, vid: getVid() });

      if (status === 404 || status === 422) {
        return;
      }

      if (writeCookies && shouldResetCookies(error)) {
        resetCookies();
        return;
      }

      if (
        name === 'AbortError' ||
        (name === 'TypeError' && ['Failed to fetch', 'Load failed', 'network error'].includes(message))
      ) {
        logger.warn('Persist visitor aborted', context, error);
        return;
      }

      addError(error, context);
    },
    [addError, logger, resetCookies, writeCookies]
  );
};

const shouldResetCookies = (error: HttpError) => {
  const { message = '', status } = error ?? {};

  return (
    status === 401 ||
    (status === 400 && message.includes('Session-ID header is required')) ||
    message === '401' ||
    message.includes('Matching session invalid or not found')
  );
};
